import { useState, useCallback, useRef, useEffect } from 'react';
import { toPng } from 'html-to-image';
import { toast } from 'react-toastify';
import { downloadBlob } from '@/utils/downloadFile';

/** Max byte-length of the slug portion of the filename. */
const MAX_SLUG_LENGTH = 40;

/**
 * Derive a safe filename slug from a human-readable title.
 * Converts to lowercase, replaces any run of non-alphanumeric chars with a
 * single dash, and trims the result to at most `MAX_SLUG_LENGTH` chars.
 */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/, '');
}

/**
 * Returns today's date in YYYY-MM-DD format using the local timezone.
 * Avoids the UTC-off-by-one that `new Date().toISOString().slice(0,10)` can
 * produce for users west of UTC.
 */
function localDateString(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface UseDownloadImageOptions {
  /** Shown in the toast and used to build the filename. */
  title?: string;
  /** Override the generated filename entirely. */
  filename?: string;
  /** Translated error message to show in the toast on failure. */
  errorMessage?: string;
  /** Pixel ratio for the exported PNG (default: 2). */
  pixelRatio?: number;
}

/**
 * Hook for "Download PNG" icon-button UX.
 *
 * Mirrors the `useCopyState` pattern: returns `[isDownloading, isDone, trigger]`.
 * - While `isDownloading` is true, render `<Loader2 className="animate-spin" />`.
 * - When `isDone` is true for ~2 s, render `<Check />`.
 * - Otherwise render the default `<Download />` icon.
 *
 * The caller passes a `React.RefObject<HTMLElement>` pointing at the node to
 * capture. `html-to-image` is used with `pixelRatio: 2` and `cacheBust: true`
 * so that web-fonts are embedded and the PNG is high-DPI.
 */
export default function useDownloadImage(
  nodeRef: React.RefObject<HTMLElement | null>,
  options: UseDownloadImageOptions = {},
): [boolean, boolean, () => void] {
  const {
    title = 'chart',
    filename,
    errorMessage = 'Failed to download image',
    pixelRatio = 2,
  } = options;

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const trigger = useCallback(async () => {
    const node = nodeRef.current;
    if (!node || isDownloading) return;

    setIsDownloading(true);
    try {
      const dataUrl = await toPng(node, { pixelRatio, cacheBust: true });
      // Convert data URL to Blob so we can use the canonical downloadBlob helper.
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const effectiveFilename =
        filename ?? `tellet-chart-${slugify(title)}-${localDateString()}.png`;
      downloadBlob(blob, effectiveFilename);

      setIsDone(true);
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
      doneTimerRef.current = setTimeout(() => {
        setIsDone(false);
        doneTimerRef.current = null;
      }, 2000);
    } catch {
      toast.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  }, [nodeRef, isDownloading, pixelRatio, filename, title, errorMessage]);

  // Clean up the done timer on unmount.
  useEffect(
    () => () => {
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
    },
    [],
  );

  return [isDownloading, isDone, trigger];
}
